/**
 * Serviço para gerenciar relatórios
 */

import { getToken } from "./tokenService";
import { API_BASE_URL } from "@/lib/apiConfig";

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: string;
  };
  message?: string;
}

export interface AnalyticsKpis {
  totalCalls: {
    value: number;
    variation: number;
    previousValue: number;
  };
  totalCost: {
    value: number;
    variation: number;
    previousValue: number;
  };
  totalTokens: number;
  successRate: {
    value: number;
    variation: number;
    previousValue: number;
  };
  avgResponseTime: {
    value: number;
    variation: number;
    previousValue: number;
  };
}

export interface TopModel {
  name: string;
  calls: number;
  cost: number;
}

export interface TopClient {
  name: string;
  calls: number;
  cost: number;
}

export interface HourlyDistribution {
  label: string;
  calls: number;
  success: number;
  error: number;
  cost: number;
}

export interface DailyTrend {
  date: string;
  calls: number;
  success: number;
  error: number;
  cost: number;
  tokens: number;
}

export interface AnalyticsData {
  actualPeriod?: {
    startDate: string;
    endDate: string;
    requestedStartDate: string;
    requestedEndDate: string;
  };
  kpis: AnalyticsKpis;
  topModels: TopModel[];
  topClients: TopClient[];
  hourlyDistribution: HourlyDistribution[];
  dailyTrend: DailyTrend[];
}

export interface Report {
  id: string;
  name: string;
  type: "api_calls" | "costs" | "tokens" | "performance" | "users" | "models";
  period: string;
  status: "generating" | "generated" | "error";
  size: string;
  createdAt: string;
  format?: string;
}

export interface ReportsListResponse {
  reports: Report[];
  total: number;
  pagination: {
    limit: number;
    offset: number;
    pages: number;
  };
}

export interface ReportDetail extends Report {
  data?: AnalyticsData;
  errorMessage?: string;
}

export interface GenerateReportRequest {
  type: "api_calls" | "costs" | "tokens" | "performance" | "users" | "models";
  period: "1h" | "24h" | "7d" | "30d" | "custom";
  client_id?: string | null;
  model_id?: string | null;
  date_from?: string | null;
  date_to?: string | null;
  format?: "pdf" | "excel" | "csv" | "json";
}

export interface AnalyticsOptions {
  period?: "1h" | "24h" | "7d" | "30d" | "custom";
  client_id?: string | null;
  model_id?: string | null;
  date_from?: string | null;
  date_to?: string | null;
  timezone?: string | null;
}

/**
 * Faz uma requisição autenticada para a API
 */
async function apiRequest<T>(
  endpoint: string,
  method: string = "GET",
  body?: any
): Promise<ApiResponse<T>> {
  const token = getToken();

  if (!token) {
    throw new Error("Token de autenticação não encontrado");
  }

  const config: RequestInit = {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  if (body && method !== "GET") {
    config.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    // Ler o texto da resposta primeiro (body só pode ser lido uma vez)
    const text = await response.text();

    // Tentar fazer parse do JSON
    let data;
    try {
      data = JSON.parse(text);
    } catch (parseError) {
      // Se não for JSON válido, retornar erro
      return {
        success: false,
        error: {
          code: "PARSE_ERROR",
          message: `Resposta não é JSON válido: ${text.substring(0, 200)}`,
        },
      };
    }

    // Verificar se a resposta HTTP foi bem-sucedida
    if (!response.ok) {
      return {
        success: false,
        error: data.error || {
          code: "HTTP_ERROR",
          message: `Erro HTTP ${response.status}: ${data.message || text.substring(0, 200)}`,
        },
      };
    }

    return data;
  } catch (error) {
    return {
      success: false,
      error: {
        code: "NETWORK_ERROR",
        message:
          error instanceof Error ? error.message : "Erro de conexão com a API",
      },
    };
  }
}

/**
 * Obtém dados de analytics
 */
export async function getAnalyticsData(
  options?: AnalyticsOptions
): Promise<ApiResponse<AnalyticsData>> {
  const params = new URLSearchParams();

  if (options?.period) {
    params.append("period", options.period);
  }
  if (options?.client_id !== undefined) {
    if (options.client_id === null) {
      params.append("client_id", "null");
    } else if (options.client_id !== "all") {
      params.append("client_id", options.client_id);
    }
  }
  if (options?.model_id !== undefined && options.model_id !== null) {
    params.append("model_id", options.model_id);
  }
  if (options?.date_from) {
    params.append("date_from", options.date_from);
  }
  if (options?.date_to) {
    params.append("date_to", options.date_to);
  }
  // Detectar timezone do navegador por padrão
  try {
    const browserTz = options?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (browserTz) params.append("timezone", browserTz);
  } catch {}

  const queryString = params.toString();
  return apiRequest<AnalyticsData>(
    `/reports/analytics${queryString ? `?${queryString}` : ""}`
  );
}

/**
 * Gera um novo relatório
 */
export async function generateReport(
  request: GenerateReportRequest
): Promise<ApiResponse<Report>> {
  return apiRequest<Report>("/reports", "POST", request);
}

/**
 * Lista relatórios gerados
 */
export async function listReports(options?: {
  limit?: number;
  offset?: number;
  search?: string;
}): Promise<ApiResponse<ReportsListResponse>> {
  const params = new URLSearchParams();

  if (options?.limit) {
    params.append("limit", options.limit.toString());
  }
  if (options?.offset) {
    params.append("offset", options.offset.toString());
  }
  if (options?.search) {
    params.append("search", options.search);
  }

  const queryString = params.toString();
  return apiRequest<ReportsListResponse>(
    `/reports${queryString ? `?${queryString}` : ""}`
  );
}

/**
 * Obtém um relatório específico
 */
export async function getReportById(
  id: string
): Promise<ApiResponse<ReportDetail>> {
  return apiRequest<ReportDetail>(`/reports/${id}`);
}

/**
 * Download de um relatório
 */
export async function downloadReport(
  id: string
): Promise<ApiResponse<{ id: string; name: string; type: string; format: string; data: any }>> {
  return apiRequest(`/reports/${id}/download`);
}

